const TZ = "Europe/Copenhagen";
const dtf = (opts) => new Intl.DateTimeFormat("da-DK", { timeZone: TZ, ...opts });

export function formatKr(ore) {
  if (ore == null) return "";
  const kr = ore / 100;
  return (
    kr.toLocaleString("da-DK", {
      minimumFractionDigits: ore % 100 ? 2 : 0,
      maximumFractionDigits: 2,
    }) + " kr."
  );
}

export function parseKr(input) {
  if (input == null || String(input).trim() === "") return null;
  const s = String(input)
    .replace(/kr\.?|dkk|\s/gi, "")
    .replace(/\.(?=\d{3}(\D|$))/g, "")
    .replace(",", ".");
  const n = Number(s);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 100);
}

export const krInput = (ore) =>
  ore == null ? "" : (ore / 100).toLocaleString("da-DK", { maximumFractionDigits: 2, useGrouping: false });

export const formatKrWhole = (ore) =>
  ore == null ? "0" : Math.round(ore / 100).toLocaleString("da-DK");

export const fmtDate = (iso) => (iso ? dtf({ weekday: "long", day: "numeric", month: "long" }).format(new Date(iso)) : "");
export const fmtShortDate = (iso) => (iso ? dtf({ weekday: "short", day: "numeric", month: "short" }).format(new Date(iso)) : "");
export const fmtTime = (iso) => (iso ? dtf({ hour: "2-digit", minute: "2-digit" }).format(new Date(iso)).replace(".", ":") : "");
export const fmtDateTime = (iso) => (iso ? `${fmtShortDate(iso)} kl. ${fmtTime(iso)}` : "");

export function fmtWindow(startIso, endIso) {
  const sameDay = fmtDate(startIso) === fmtDate(endIso);
  return sameDay
    ? `${fmtDate(startIso)} kl. ${fmtTime(startIso)}–${fmtTime(endIso)}`
    : `${fmtDateTime(startIso)} – ${fmtDateTime(endIso)}`;
}

const dayKey = (iso) => new Date(iso).toLocaleDateString("da-DK", { timeZone: "Europe/Copenhagen" });
const shortDay = (iso) => {
  const d = new Date(iso);
  const wd = d.toLocaleDateString("da-DK", { weekday: "short", timeZone: "Europe/Copenhagen" }).replace(/\.$/, "");
  const dm = d.toLocaleDateString("da-DK", { day: "numeric", month: "numeric", timeZone: "Europe/Copenhagen" }).replace(/\./g, "/").replace(/\/$/, "");
  return `${wd}. ${dm}`;
};

// Compact range for cards: time only when same day and today; dates when not today or multi-day.
export function fmtRange(startIso, endIso) {
  if (!startIso) return "";
  const today = dayKey(new Date().toISOString());
  if (!endIso || dayKey(startIso) === dayKey(endIso)) {
    const t = `${fmtTime(startIso)}${endIso ? `–${fmtTime(endIso)}` : ""}`;
    return dayKey(startIso) === today ? t : `${shortDay(startIso)} ${t}`;
  }
  return `${shortDay(startIso)} ${fmtTime(startIso)} – ${shortDay(endIso)} ${fmtTime(endIso)}`;
}

export function fmtFrom(iso) {
  return dayKey(iso) === dayKey(new Date().toISOString()) ? `kl. ${fmtTime(iso)}` : `${shortDay(iso)} kl. ${fmtTime(iso)}`;
}

export function relTime(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "lige nu";
  if (diff < 3600) return `${Math.floor(diff / 60)} min. siden`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} t. siden`;
  return fmtDateTime(iso);
}

export function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function fromLocalInput(s) {
  if (!s) return null;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

export const initials = (n) =>
  String(n || "?")
    .split(/\s+/)
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

const WD_S = ["søn.", "man.", "tir.", "ons.", "tor.", "fre.", "lør."];
const MON_S = ["jan.", "feb.", "mar.", "apr.", "maj", "jun.", "jul.", "aug.", "sep.", "okt.", "nov.", "dec."];
const EN_WD = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const cph = (iso) => {
  const p = Object.fromEntries(
    new Intl.DateTimeFormat("en-US", { timeZone: TZ, weekday: "short", day: "numeric", month: "numeric" }).formatToParts(new Date(iso)).map((x) => [x.type, x.value])
  );
  return { wd: EN_WD.indexOf(p.weekday), d: +p.day, m: +p.month };
};
export const fmtSlotDay = (iso) => {
  const p = cph(iso);
  return `${WD_S[p.wd]} ${p.d}. ${MON_S[p.m - 1]}`;
};
export const slotsOf = (o) =>
  o?.pickup_slots?.length ? o.pickup_slots : o?.pickup_start ? [{ start: o.pickup_start, end: o.pickup_end }] : [];
export const fmtSlot = (s) =>
  dayKey(s.start) === dayKey(s.end)
    ? `${fmtSlotDay(s.start)} ${fmtTime(s.start)}-${fmtTime(s.end)}`
    : `${fmtSlotDay(s.start)} ${fmtTime(s.start)} - ${fmtSlotDay(s.end)} ${fmtTime(s.end)}`;
// "tir. 27. okt. 14:00-20:00, ons. 28. okt. 10:00-20:00 & tor. 29. okt. 10:00-16:00" (same as the Messenger text)
export function fmtSlots(slots) {
  const parts = (slots || []).map(fmtSlot);
  return parts.length < 2 ? parts[0] || "" : `${parts.slice(0, -1).join(", ")} & ${parts[parts.length - 1]}`;
}
export const isToday = (iso) => dayKey(iso) === dayKey(new Date().toISOString());
